import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Button, Box, Grid, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import useConstant from "use-constant";
import { useDispatch } from "react-redux";
import isEqual from "lodash/isEqual";

// Formik
import { Formik, Form, Field, useFormikContext } from "formik";
import { TextField } from "formik-mui";
import Select from "../../components/FormikFields/Select";

//Custom
import Modal from "../../components/Modal";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import CourseFlags from "../../components/Icons/CourseFlags";
import Table from "../../components/Table/Table";
import { courseTypes } from "../../constants";
import { useCourses } from "../../hooks/assignments";
import { getSortingParam } from "../../utils/tableUtils";
import useTableState from "../../hooks/useTableState";
import { updateTableStatus } from "../../redux/actions/tablesActions";
import usePermissions from "../../redux/selectors/usePermissions";

const allColumns = {
  courseType: {
    title: "Type",
    field: "courseType",
    cellStyle: {
      width: 40,
      textAlign: "center"
    },
    render: ({ courseType }) => (
      <CourseTypeIcon
        type={
          courseType === courseTypes.online || courseType === courseTypes.policy
            ? courseType
            : courseTypes.ilt
        }
      />
    ),
    customSort: () => 0
  },
  title: {
    title: "Title",
    field: "name",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 100
    },
    render: ({ name, flags }) => (
      <Box display="flex" alignItems="center">
        <Box mr={2}>{name}</Box>
        {flags && <CourseFlags flags={flags} />}
      </Box>
    )
  },
  number: {
    title: "number",
    field: "number",
    cellStyle: {
      width: 100,
      textAlign: "center"
    },
    render: ({ number }) => <Box>{number}</Box>,
    customSort: () => 0
  }
};

const defaultFiltersState = {
  type: "",
  searchName: ""
};

const TrainingObjectsFilters = ({ modalOpen }) => {
  const { t } = useTranslation();
  const { resetForm, submitForm, setSubmitting } = useFormikContext();
  const { hasPolicyPartnerAdminAccess } = usePermissions();

  useEffect(() => {
    if (!modalOpen) {
      resetForm();
    }
  }, [modalOpen]);

  const types = [
    { label: "Show All", value: "all" },
    { label: courseTypes.online, value: "online" },
    { label: courseTypes.instructorLedTraining, value: "ilt" }
  ];
  if (hasPolicyPartnerAdminAccess) {
    types.push({ label: courseTypes.policy, value: "policy" });
  }

  const handleSubmitForm = () => {
    submitForm();
    setSubmitting(false);
  };

  const debouncedSubmit = useConstant(() => debounce(handleSubmitForm, 500));

  return (
    <Form>
      <Grid container spacing={2} sx={{ alignItems: "center", mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Select
            name="type"
            menuItems={types}
            submitOnChange={true}
            label={t("specialAssignments.type")}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <Field
            fullWidth
            label={t("specialAssignments.searchName")}
            variant="filled"
            component={TextField}
            name={t("searchName")}
            onKeyUp={debouncedSubmit}
            type="text"
            disabled={false}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
              disableUnderline: true
            }}
          />
        </Grid>
      </Grid>
    </Form>
  );
};

const TrainingObjectsPickerModal = ({
  onClose,
  open,
  onSelectTrainingObject
}) => {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const dispatch = useDispatch();
  const { hasPolicyPartnerAdminAccess } = usePermissions();
  const tableState = useTableState("pickableCourses");
  const [externalFilter, setExternalFilter] = useState(tableState.filters);
  const [isFetched, setIsFetched] = useState(false);
  const { getCourses } = useCourses("special", null);

  const columns = hasPolicyPartnerAdminAccess
    ? [allColumns.courseType, allColumns.number, allColumns.title]
    : [allColumns.courseType, allColumns.title];

  useEffect(() => {
    if (tableRef.current) {
      const query = { ...tableRef.current.state.query, page: 0 };
      tableRef.current.onQueryChange(query);
    }
  }, [externalFilter]);

  useEffect(() => {
    setExternalFilter(tableState.filters);
  }, []);

  const filter = newFilters => {
    const filters = {
      ...externalFilter,
      ...newFilters
    };

    if (!isEqual(externalFilter, filters)) {
      setExternalFilter(filters);
    }
  };

  const handleClearFilters = () => {
    setExternalFilter(defaultFiltersState);
    onClose();
  };

  const handleRowClick = (e, rowData) => {
    onSelectTrainingObject(rowData);
  };

  const fetchCourses = async tableQueryData => {
    if (!isFetched) {
      tableQueryData.page = tableState.page;
    }
    dispatch(
      updateTableStatus("pickableCourses", tableQueryData, externalFilter)
    );

    const orderByColumn = tableQueryData.orderBy
      ? tableQueryData.orderBy.field
      : null;
    let records = await getCourses({
      page: { number: tableQueryData.page, size: tableQueryData.pageSize },
      pageSize: tableQueryData.pageSize,
      sort: getSortingParam(orderByColumn, tableQueryData.orderDirection),
      filters: { ...externalFilter }
    });
    setIsFetched(true);
    // Material UI Refactor
    // Material Table needs the page to be 0 indexed
    records.page -= 1;
    return records;
  };

  return (
    <>
      <Modal
        scrollable
        disableEnforceFocus
        showCloseIcon
        maxWidth="lg"
        fullWidth
        fillHeight
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {t("specialAssignments.addSpecialAssignment")}
          </Typography>
        }
        dialogContent={
          <>
            <Box display="flex" flexDirection="column" mb={1}>
              <Formik
                initialValues={externalFilter}
                enableReinitialize
                onSubmit={filter}
              >
                {() => {
                  return <TrainingObjectsFilters modalOpen={open} />;
                }}
              </Formik>
              <Table
                itemType="record"
                columns={columns}
                onRowClick={(e, rowData) => handleRowClick(e, rowData)}
                data={fetchCourses}
                tableRef={tableRef}
                initialPage={tableState.page}
                defaultPerPage={tableState.pageSize}
              />
            </Box>
          </>
        }
        dialogActions={
          <>
            <Button color="primary" onClick={handleClearFilters}>
              {t("cancelBtn")}
            </Button>
          </>
        }
        open={open}
        onClose={handleClearFilters}
      />
    </>
  );
};

TrainingObjectsPickerModal.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool
};

export default TrainingObjectsPickerModal;
